// Port of BivouacEngine/BivouacScoringConfiguration.swift.

import type { EpochMs } from '../core/models';

/** Relative weights of the physical score components (normalised by their sum). */
export interface BivouacScoringWeights {
  /** Slope (flatness) weight. */
  slope: number;
  /** Land-cover weight. */
  landCover: number;
  /** Water proximity weight (usable source nearby vs. waterway too close). */
  waterProximity: number;
  /** Discretion weight (distance from trail, settlements, roads). */
  discretion: number;
  /** Exposure / elevation weight. */
  exposure: number;
}

/**
 * Thresholds and weights of the physical (terrain) and weather scoring.
 *
 * Physical score = weighted mean of five component scores (each 0…1):
 *
 * | component | weight | inputs |
 * |---|---|---|
 * | slope | 0.35 | ≤ 5° ideal, ≤ 10° acceptable, ≤ 15° poor, > 15° unsuitable (hard veto) |
 * | land cover | 0.20 | meadow best; glacier / water / wetland / settlement / cultivated → hard veto |
 * | water proximity | 0.10 | water source within 600 m positive; waterway < 30 m strongly negative, < 10 m hard veto |
 * | discretion | 0.15 | trail 60–800 m preferred (< 30 m negative); settlement < 250 m, road < 150 m negative |
 * | exposure / elevation | 0.20 | wind shelter; > 2600 m caution (cold / exposure) |
 *
 * Two caps then make the banding explicit: any *caution* or *negative* factor caps the score at `cautionScoreCap` so it
 * can never be green; any *hard veto* sets `hasHardHazard` and caps the score at `hardVetoScoreCap` so the physical
 * banding is always "poor".
 */
export interface BivouacScoringConfiguration {
  // Slope
  /** Slopes up to this value (degrees) are ideal for a tent. */
  idealSlopeDegrees: number;
  /** Slopes up to this value are acceptable. */
  acceptableSlopeDegrees: number;
  /** Slopes above this value are unsuitable (hard veto); between acceptable and this value: poor. */
  maxSlopeDegrees: number;

  // Water
  /** Closer than this to a waterway is a hard veto (flood risk, riparian protection). */
  waterwayMinimumMeters: number;
  /** Closer than this to a waterway is strongly negative (environment / flood) and caps the score. */
  waterwaySensitiveMeters: number;
  /** A water source within this distance is a positive factor. */
  waterSourceRadiusMeters: number;
  /** A water source within this distance is still mentioned (neutral). */
  waterSourceFarRadiusMeters: number;

  // Discretion / access
  /** Closer than this to the trail is negative (visibility, trampling). */
  trailTooCloseMeters: number;
  /** Lower bound of the preferred distance from the trail. */
  preferredMinimumTrailDistanceMeters: number;
  /** Upper bound of the preferred distance from the trail. */
  preferredMaximumTrailDistanceMeters: number;
  /** Closer than this to a settlement is negative. */
  settlementMinimumMeters: number;
  /** Closer than this to a road is negative. */
  roadMinimumMeters: number;

  // Elevation / environment
  /** Above this elevation (m) conditions are treated as caution (cold, exposure). */
  cautionElevationMeters: number;
  /** Above this elevation, alpine meadows / pastures are environmentally highly sensitive. */
  highAlpineMeadowElevationMeters: number;
  /** Months (1…12) in which avalanche terrain is a hard veto; outside them it is a caution. */
  avalancheSeasonMonths: number[];

  // Score shaping
  /** Component weights. */
  weights: BivouacScoringWeights;
  /** Maximum score when a caution-level factor is present (keeps the result out of the green band). */
  cautionScoreCap: number;
  /** Maximum score when a hard veto is present (physical banding "poor"). */
  hardVetoScoreCap: number;

  // Weather
  /** Gusts (km/h) at or above which wind is a planner warning and strongly lowers the weather score. */
  strongWindGustKmh: number;
  /**
   * Mean wind (km/h) at or above which wind is a planner warning when the forecast has no gusts (MET Norway outside
   * its Nordic area, i.e. all of the Alps). The suitability score scales a mean wind by
   * `strongWindGustKmh / strongMeanWindKmh` before applying the gust bands.
   */
  strongMeanWindKmh: number;
  /** Night temperatures (°C) below this value trigger a cold-night warning. */
  coldNightCelsius: number;
}

function makeDefault(): BivouacScoringConfiguration {
  return {
    idealSlopeDegrees: 5,
    acceptableSlopeDegrees: 10,
    maxSlopeDegrees: 15,
    waterwayMinimumMeters: 10,
    waterwaySensitiveMeters: 30,
    waterSourceRadiusMeters: 600,
    waterSourceFarRadiusMeters: 1500,
    trailTooCloseMeters: 30,
    preferredMinimumTrailDistanceMeters: 60,
    preferredMaximumTrailDistanceMeters: 800,
    settlementMinimumMeters: 250,
    roadMinimumMeters: 150,
    cautionElevationMeters: 2600,
    highAlpineMeadowElevationMeters: 2400,
    avalancheSeasonMonths: [12, 1, 2, 3, 4, 5],
    weights: { slope: 0.35, landCover: 0.2, waterProximity: 0.1, discretion: 0.15, exposure: 0.2 },
    cautionScoreCap: 0.6,
    hardVetoScoreCap: 0.2,
    strongWindGustKmh: 60,
    strongMeanWindKmh: 40,
    coldNightCelsius: 0,
  };
}

const frozenDefault = makeDefault();
Object.freeze(frozenDefault.weights);
Object.freeze(frozenDefault.avalancheSeasonMonths);
Object.freeze(frozenDefault);

export const BivouacScoringConfiguration = {
  /** The configuration documented in BIVOUAC_LOGIC.md (deeply frozen). */
  default: frozenDefault as Readonly<BivouacScoringConfiguration>,

  /** A fresh, mutable copy of the default configuration with `overrides` applied. */
  make(overrides: Partial<BivouacScoringConfiguration> = {}): BivouacScoringConfiguration {
    return { ...makeDefault(), ...overrides };
  },

  /** Whether avalanche terrain is a hard veto on `date` (UTC calendar month). */
  isAvalancheSeason(configuration: Readonly<BivouacScoringConfiguration>, date: EpochMs): boolean {
    return configuration.avalancheSeasonMonths.includes(new Date(date).getUTCMonth() + 1);
  },
};
